/**
 * Auth token / session storage abstraction.
 *
 * WEB: a transparent passthrough to localStorage — behavior is byte-for-byte
 * identical to before, so the web app is unchanged.
 *
 * NATIVE (Capacitor): the JWT and session keys are kept in @capacitor/preferences
 * (app-private, not the plaintext WebView localStorage) plus a synchronous
 * in-memory mirror so the existing synchronous call sites (the axios request
 * interceptor, the zustand store's eager init) keep working without becoming
 * async. `hydrate()` loads Preferences into the mirror once at boot, migrating
 * any legacy localStorage token into secure storage on first run.
 *
 * Only the auth/session keys route through here. UI prefs (theme, language, …)
 * stay on localStorage.
 */
import { isNative } from '../utils/platform';

export const AUTH_KEYS = [
  'authToken', 'user', 'centerId', 'centerType', 'learningSubType',
  'memberships', 'defaultCenterId',
];

// Native synchronous mirror of the Preferences values.
const mem = new Map();

let _prefs = null;
async function getPrefs() {
  if (_prefs) return _prefs;
  const mod = await import('@capacitor/preferences');
  _prefs = mod.Preferences;
  return _prefs;
}

export function getItem(key) {
  if (!isNative) return localStorage.getItem(key);
  return mem.has(key) ? mem.get(key) : null;
}

export function setItem(key, value) {
  if (!isNative) { localStorage.setItem(key, value); return; }
  mem.set(key, value);
  // Persist asynchronously; the mirror already reflects the new value so reads
  // are immediately correct.
  getPrefs().then((p) => p.set({ key, value })).catch(() => { /* best effort */ });
}

export function removeItem(key) {
  if (!isNative) { localStorage.removeItem(key); return; }
  mem.delete(key);
  getPrefs().then((p) => p.remove({ key })).catch(() => { /* best effort */ });
}

/**
 * Load secure storage into the in-memory mirror. Call once before first render
 * on native (no-op on web). Migrates any legacy localStorage auth values into
 * Preferences and clears them from plaintext localStorage.
 */
export async function hydrate() {
  if (!isNative) return;
  const p = await getPrefs();
  for (const key of AUTH_KEYS) {
    let { value } = await p.get({ key });
    if (value == null && typeof localStorage !== 'undefined') {
      const legacy = localStorage.getItem(key);
      if (legacy != null) {
        value = legacy;
        await p.set({ key, value: legacy });
        try { localStorage.removeItem(key); } catch { /* ignore */ }
      }
    }
    if (value != null) mem.set(key, value);
  }
}
